import * as React from "react";
import PropTypes from "prop-types";
import { FixedSizeList } from "react-window";

const LISTBOX_PADDING = 8;

function renderRow({ data, index, style }) {
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top ?? 0) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext(null);

const OuterElementType = React.forwardRef(function OuterElementType(props, ref) {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

OuterElementType.displayName = "VirtualizedAutocompleteOuterElement";

/**
 * Listbox for MUI Autocomplete (react-window FixedSizeList).
 * Renders all filtered options without a hard cap; only visible rows touch the DOM.
 */
const VirtualizedAutocompleteListbox = React.forwardRef(function VirtualizedAutocompleteListbox(
  props,
  ref
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 36;

  const height = React.useMemo(() => {
    if (itemCount === 0) {
      return LISTBOX_PADDING * 2;
    }
    return Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING;
  }, [itemCount, itemSize]);

  if (itemCount === 0) {
    return (
      <div ref={ref} {...other}>
        <ul className="MuiAutocomplete-listbox" style={{ padding: 0, margin: 0 }} />
      </div>
    );
  }

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          itemData={itemData}
          height={height}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

VirtualizedAutocompleteListbox.propTypes = {
  children: PropTypes.node,
};

export default VirtualizedAutocompleteListbox;
